import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Shield, ShieldAlert, CheckSquare, ClipboardList, AlertTriangle,
  BarChart2, ClipboardCheck, Users, Palette, CheckSquare2,
  LayoutDashboard, ArrowRight, X, Upload, FileText,
} from 'lucide-react';
import clsx from 'clsx';
import { useAuthStore } from '@/store/auth.store';
import { useBrandingStore } from '@/store/branding.store';
import { LOGO_ICON } from '@/hooks/useThemedLogo';
import { UserRole } from '@/types';

const ROLE_LABELS: Record<UserRole, string> = {
  ADMIN:              'Administrator',
  RISK_MANAGER:       'Risk Manager',
  COMPLIANCE_OFFICER: 'Compliance Officer',
  AUDITOR:            'Internal Auditor',
  VIEWER:             'Read-only Viewer',
  CLIENT_ADMIN:       'Client Administrator',
  CLIENT_USER:        'Client User',
};

const ROLE_DESCRIPTIONS: Record<UserRole, string> = {
  ADMIN:              'Full platform access — user management, framework import, branding and all GRC modules.',
  RISK_MANAGER:       'Risk register, control mapping, task assignment and remediation tracking.',
  COMPLIANCE_OFFICER: 'Framework assessments, compliance dashboards and audit evidence management.',
  AUDITOR:            'Audit programmes, findings, risk register review and assessment reports.',
  VIEWER:             'Read-only access to dashboards, risk registers and compliance reports.',
  CLIENT_ADMIN:       'Manage and submit your organisation assessment responses and evidence in the portal.',
  CLIENT_USER:        'Contribute assessment responses and upload evidence in the client portal.',
};

interface ActionCard {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  desc:  string;
  to:    string;
  cta:   string;
}

const ROLE_ACTIONS: Record<UserRole, ActionCard[]> = {
  ADMIN: [
    { icon: Users,          title: 'User Management',       desc: 'Invite team members and assign roles',        to: '/admin/users',      cta: 'Open Users'       },
    { icon: Upload,         title: 'Import a Framework',    desc: 'Add SOC 2, ISO 27001, NIST, HIPAA and more', to: '/admin/frameworks', cta: 'Import Framework' },
    { icon: ClipboardCheck, title: 'Start an Assessment',   desc: 'Evaluate compliance against a framework',     to: '/assessments',      cta: 'New Assessment'   },
    { icon: ShieldAlert,    title: 'Risk Register',         desc: 'Register and score organisational risks',     to: '/risks',            cta: 'Add Risk'         },
    { icon: BarChart2,      title: 'Compliance Dashboard',  desc: 'Executive overview of compliance posture',    to: '/compliance',       cta: 'View Dashboard'   },
    { icon: Palette,        title: 'Platform Branding',     desc: 'Configure logo, colours and platform name',   to: '/admin/branding',   cta: 'Configure'        },
  ],
  RISK_MANAGER: [
    { icon: ShieldAlert,    title: 'Risk Register',         desc: 'Register and score a new risk',               to: '/risks',            cta: 'Add Risk'         },
    { icon: CheckSquare,    title: 'Controls',              desc: 'Link controls to risks and track status',     to: '/controls',         cta: 'Add Control'      },
    { icon: AlertTriangle,  title: 'Findings',              desc: 'Track open findings and drive remediation',   to: '/findings',         cta: 'View Findings'    },
    { icon: CheckSquare2,   title: 'Task Management',       desc: 'Assign action items to team members',         to: '/tasks',            cta: 'Add Task'         },
    { icon: ClipboardList,  title: 'Audit Programme',       desc: 'Monitor audit status, scope and outcomes',    to: '/audits',           cta: 'View Audits'      },
    { icon: BarChart2,      title: 'Compliance Dashboard',  desc: 'Monitor scores across all frameworks',        to: '/compliance',       cta: 'View Dashboard'   },
  ],
  COMPLIANCE_OFFICER: [
    { icon: ClipboardCheck, title: 'Assessments',           desc: 'Evaluate compliance against a framework',     to: '/assessments',      cta: 'New Assessment'   },
    { icon: BarChart2,      title: 'Compliance Dashboard',  desc: 'Monitor compliance posture at a glance',      to: '/compliance',       cta: 'View Dashboard'   },
    { icon: Shield,         title: 'Frameworks',            desc: 'Review active frameworks and requirements',   to: '/frameworks',       cta: 'View Frameworks'  },
    { icon: ClipboardList,  title: 'Audit Management',      desc: 'Track audit programmes and manage evidence',  to: '/audits',           cta: 'View Audits'      },
    { icon: AlertTriangle,  title: 'Findings',              desc: 'Monitor and drive closure of open findings',  to: '/findings',         cta: 'View Findings'    },
    { icon: FileText,       title: 'Policy Management',     desc: 'Maintain policy library and control links',   to: '/policies',         cta: 'View Policies'    },
  ],
  AUDITOR: [
    { icon: ClipboardList,  title: 'New Audit',             desc: 'Start an audit programme with defined scope', to: '/audits',           cta: 'New Audit'        },
    { icon: AlertTriangle,  title: 'Log a Finding',         desc: 'Document audit issues and observations',      to: '/findings',         cta: 'Add Finding'      },
    { icon: ShieldAlert,    title: 'Risk Register',         desc: 'Examine the organisational risk register',    to: '/risks',            cta: 'View Risks'       },
    { icon: ClipboardCheck, title: 'Assessment Reports',    desc: 'Review compliance assessment results',        to: '/assessments',      cta: 'View Assessments' },
    { icon: CheckSquare2,   title: 'Task Tracking',         desc: 'Monitor remediation tasks and follow-ups',    to: '/tasks',            cta: 'View Tasks'       },
    { icon: BarChart2,      title: 'Compliance Dashboard',  desc: 'Review compliance scores and coverage gaps',  to: '/compliance',       cta: 'View Dashboard'   },
  ],
  VIEWER: [
    { icon: LayoutDashboard, title: 'GRC Dashboard',        desc: 'Overview of risks, controls and compliance',  to: '/dashboard',        cta: 'View Dashboard'   },
    { icon: ShieldAlert,    title: 'Risk Register',         desc: 'Browse the organisational risk register',     to: '/risks',            cta: 'Browse Risks'     },
    { icon: BarChart2,      title: 'Compliance Dashboard',  desc: 'Compliance scores across all frameworks',     to: '/compliance',       cta: 'View Compliance'  },
    { icon: Shield,         title: 'Frameworks',            desc: 'Explore the frameworks your org uses',        to: '/frameworks',       cta: 'View Frameworks'  },
    { icon: ClipboardCheck, title: 'Assessments',           desc: 'Browse compliance assessment results',        to: '/assessments',      cta: 'View Assessments' },
    { icon: AlertTriangle,  title: 'Findings',              desc: 'Review open findings and remediation status', to: '/findings',         cta: 'View Findings'    },
  ],
  CLIENT_ADMIN: [
    { icon: LayoutDashboard, title: 'Portal Dashboard',     desc: 'Track active engagements and progress',       to: '/portal/dashboard',   cta: 'Open Dashboard'   },
    { icon: ClipboardCheck,  title: 'Engagements',          desc: 'Respond to requirements and upload evidence', to: '/portal/engagements', cta: 'Open Engagements' },
  ],
  CLIENT_USER: [
    { icon: LayoutDashboard, title: 'Portal Dashboard',     desc: 'Track active engagements and progress',       to: '/portal/dashboard',   cta: 'Open Dashboard'   },
    { icon: ClipboardCheck,  title: 'Engagements',          desc: 'Respond to requirements and upload evidence', to: '/portal/engagements', cta: 'Open Engagements' },
  ],
};

/** Platform module overview shown on the first step for all roles */
const PLATFORM_MODULES = [
  { icon: ShieldAlert,    title: 'Risk Management',     desc: 'Register, score and treat organisational risks' },
  { icon: CheckSquare,    title: 'Controls',            desc: 'Map controls to risks and assess effectiveness' },
  { icon: ClipboardCheck, title: 'Assessments',         desc: 'Evaluate frameworks and evidence compliance' },
  { icon: ClipboardList,  title: 'Audit Management',    desc: 'Manage programmes, findings and evidence' },
  { icon: BarChart2,      title: 'Compliance',          desc: 'Cross-framework posture and gap analysis' },
  { icon: FileText,       title: 'Policy Management',   desc: 'Maintain policy library and link to controls' },
];

interface WizardProps {
  onDismiss: () => void;
}

export default function SetupWizard({ onDismiss }: WizardProps) {
  const [step, setStep] = useState(0);
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const { appName } = useBrandingStore();

  const role      = (user?.role ?? 'VIEWER') as UserRole;
  const roleLabel = ROLE_LABELS[role];
  const roleDesc  = ROLE_DESCRIPTIONS[role];
  const actions   = ROLE_ACTIONS[role] ?? ROLE_ACTIONS.VIEWER;
  const fullName  = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || 'User';

  const handleAction = (to: string) => {
    onDismiss();
    navigate(to);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div className="w-full max-w-3xl bg-surface rounded-xl shadow-2xl overflow-hidden flex max-h-[88vh]">

        {/* ── Left identity panel ── */}
        <div className="w-60 flex-shrink-0 bg-slate-900 flex flex-col px-6 py-7 overflow-y-auto">
          {/* Brand mark */}
          <div className="flex items-center gap-2.5 mb-7">
            {/* The square app icon: a small space on a navy rail. */}
            <img src={LOGO_ICON} alt="" className="h-9 w-9 rounded-sm object-contain shrink-0" />
            <span className="text-sm font-semibold text-white truncate leading-tight">{appName}</span>
          </div>

          {/* Divider */}
          <div className="border-t border-slate-700 mb-6" />

          {/* User greeting */}
          <div className="flex-1">
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Welcome back</p>
            <p className="text-xl font-bold text-white leading-tight">{fullName}</p>
            <span className="mt-3 inline-block rounded-sm bg-brand-600 px-2.5 py-0.5 text-xs font-semibold text-white tracking-wide">
              {roleLabel}
            </span>
            <p className="mt-4 text-xs text-slate-400 leading-relaxed">{roleDesc}</p>
          </div>

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-slate-700">
            <p className="text-xs font-medium text-slate-500">Governance · Risk · Compliance</p>
            <p className="text-xs text-slate-600 mt-0.5">Enterprise Platform</p>
          </div>
        </div>

        {/* ── Right content panel ── */}
        <div className="flex flex-1 flex-col min-w-0 overflow-hidden">
          {/* Panel header */}
          <div className="flex items-center justify-between px-7 py-4 border-b border-gray-100 shrink-0">
            <div>
              {step === 0 ? (
                <>
                  <h2 className="text-sm font-semibold text-gray-900 tracking-tight">Platform Overview</h2>
                  <p className="text-xs text-gray-500 mt-0.5">Key modules included in this deployment</p>
                </>
              ) : (
                <>
                  <h2 className="text-sm font-semibold text-gray-900 tracking-tight">Quick Start</h2>
                  <p className="text-xs text-gray-500 mt-0.5">Select a module to get started immediately</p>
                </>
              )}
            </div>
            <button
              onClick={onDismiss}
              className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
              title="Dismiss"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Panel body */}
          <div className="flex-1 overflow-y-auto px-7 py-5">
            {step === 0 ? (
              <div className="grid grid-cols-2 gap-2.5">
                {PLATFORM_MODULES.map(({ icon: Icon, title, desc }) => (
                  <div
                    key={title}
                    className="flex items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 hover:border-brand-200 hover:bg-surface transition-colors"
                  >
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-surface border border-gray-200 shadow-sm">
                      <Icon className="h-4 w-4 text-gray-500" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900">{title}</p>
                      <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2.5">
                {actions.map(({ icon: Icon, title, desc, to, cta }) => (
                  <button
                    key={title}
                    onClick={() => handleAction(to)}
                    className="group text-left rounded-lg border border-gray-200 bg-surface p-4 hover:border-brand-400 hover:shadow-sm transition-all flex flex-col gap-2"
                  >
                    <div className="flex items-center gap-2.5">
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-gray-100 group-hover:bg-brand-50 transition-colors">
                        <Icon className="h-4 w-4 text-gray-500 group-hover:text-brand-600 transition-colors" />
                      </div>
                      <p className="text-sm font-semibold text-gray-900">{title}</p>
                    </div>
                    <p className="text-xs text-gray-500 leading-relaxed">{desc}</p>
                    <div className="flex items-center gap-1 text-xs font-medium text-brand-600 mt-auto pt-1">
                      {cta} <ArrowRight className="h-3 w-3" />
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Panel footer */}
          <div className="border-t border-gray-100 px-7 py-4 flex items-center justify-between shrink-0 bg-surface">
            {/* Step indicator */}
            <div className="flex items-center gap-1.5">
              {[0, 1].map((i) => (
                <div
                  key={i}
                  className={clsx(
                    'h-1 rounded-full transition-all',
                    i === step ? 'w-6 bg-brand-600' : 'w-2 bg-gray-200',
                  )}
                />
              ))}
            </div>

            <div className="flex items-center gap-4">
              {step === 0 ? (
                <>
                  <button
                    onClick={onDismiss}
                    className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    Dismiss
                  </button>
                  <button
                    onClick={() => setStep(1)}
                    className="btn-primary text-sm flex items-center gap-1.5"
                  >
                    Quick Start <ArrowRight className="h-3.5 w-3.5" />
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => setStep(0)}
                    className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    ← Overview
                  </button>
                  <button
                    onClick={onDismiss}
                    className="btn-primary text-sm"
                  >
                    Launch Platform
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}

